import CertipersUser from "@/models/User";
import { list, listAll, retrieve } from "@/utils/factoryRest";
import { setDisplayedItems } from "@/utils/setDisplayedItems";
import { formatRole } from "./role";
import { defineStore } from "pinia";
import { useOidcStore } from "vue3-oidc";
import { useRepo } from "pinia-orm";
import dayjs from "dayjs";
import { toRaw } from "vue";

import { UsersApi } from "@socotec.io/certipers-back-sdk";
import configs from "@/setup/openApiConfig";

const usersApi = new UsersApi(configs.certipersBackConfig);

const userRepo = useRepo(CertipersUser);

// A.L 12/08/26 the user text fields are non-nullable on the back, so an optional
// field left empty, or cleared with Vuetify's clear button, must be sent as "".
// birthDate is the only nullable one and is reassigned separately.
const nullFieldsToEmptyString = (data) =>
  Object.fromEntries(Object.entries(data).map(([key, value]) => [key, value ?? ""]));

export const formatUser = (user) => {
  const rolesNested = typeof user.roles === "object";
  const groupsListNested = typeof user.groups === "object";
  return {
    ...user,
    ...(user.birthDate && {
      birthDate: dayjs(user.birthDate).format("YYYY-MM-DD"),
    }),
    // Roles Nested
    ...(rolesNested && {
      roles: user.roles.map(formatRole),
    }),
    // Groups Nested
    ...(groupsListNested && {
      groups: user.groups,
    }),
  };
};

export const useUserStore = defineStore("user", {
  state: () => ({
    itemsCount: 0,
    unreadNotificationCount: 0,
    shouldCallDuplicatedUser: true,
    displayUserDuplicatedModal: false,
  }),

  actions: {
    async fetchCurrentUser() {
      const oidcStore = useOidcStore();
      const { email } = oidcStore.state.value.user.profile;
      const response = await usersApi.retrieveByEmail({
        email: email,
      });
      const currentUser = response;
      return userRepo.save({
        ...formatUser(currentUser),
        isCurrentUser: true,
      });
    },

    async partialUpdateUser({ user, fields }) {
      user = structuredClone(toRaw(user));

      const payload = nullFieldsToEmptyString(
        Object.fromEntries(fields.map((field) => [field, user[field]])),
      );

      if ("birthDate" in payload) {
        payload.birthDate = user.birthDate ? new Date(user.birthDate) : null;
      }

      const response = await usersApi.partialUpdate({
        uuid: user.uuid,
        patchedUserNestedRequest: payload,
      });
      return userRepo.save(formatUser(response));
    },

    async associateQrCode({ user, file }) {
      const response = await usersApi.qrCode({ uuid: user.uuid, file });
      return userRepo.save(formatUser(response));
    },

    async submitRegistration({ certipersUser, captcha }) {
      const birthDateFormated = new Date(certipersUser.birthDate);
      const response = await usersApi.register({
        userRegistrationRequest: {
          ...nullFieldsToEmptyString(certipersUser),
          birthDate: birthDateFormated,
          clientId: import.meta.env.VITE_APP_SSO_CLIENT_ID_FRONT,
          redirectUri: import.meta.env.VITE_APP_FRONT_URL,
          captcha: captcha,
        },
      });
      return response;
    },

    fetchUsers: list({
      ModelRepo: userRepo,
      // .bind(usersApi) preserves `this` context when the method is passed as a reference
      restListMethod: usersApi.list.bind(usersApi),
      handleResponseData: ({ results }) => {
        return results.map(formatUser);
      },
      onItemsInserted: (response) => {
        setDisplayedItems(userRepo, response.results);
        useUserStore().setItemsCount(response.count);
        return response.results;
      },
    }),

    async fetchAllUsers({ filters = {} } = {}) {
      const results = await listAll(usersApi.list.bind(usersApi), filters);
      const users = results.map(formatUser);
      userRepo.save(users);
      setDisplayedItems(userRepo, results);
      this.setItemsCount(results.length);

      return users;
    },

    async fetchAllUsersWithoutModel({ filters = {} } = {}) {
      return await listAll(usersApi.list.bind(usersApi), filters);
    },

    async fetchUsersWithoutModel({ filters = {}, page = 1, pageSize = 10 }) {
      const response = await usersApi.list({
        ...filters,
        page: page,
        pageSize: pageSize,
      });

      return response.results;
    },

    retrieveUser: retrieve({
      ModelRepo: userRepo,
      restRetrieveMethod: usersApi.retrieve.bind(usersApi),
      handleResponseData: (response) => {
        return formatUser(response);
      },
    }),

    setItemsCount(count) {
      this.itemsCount = count;
    },

    async getIsDuplicateUser() {
      const response = await usersApi.isDuplicated();

      this.setDuplicatedUserModal(response.isDuplicatedAccount);
      this.setShouldCallDuplicatedUser(false);
    },

    setUnreadNotification(count) {
      this.unreadNotificationCount = count;
    },

    setShouldCallDuplicatedUser(value) {
      this.shouldCallDuplicatedUser = value;
    },

    setDuplicatedUserModal(value) {
      this.displayUserDuplicatedModal = value;
    },
  },

  getters: {
    getCurrentUser: () => userRepo.with("roles").where("isCurrentUser", true).first(),

    getUserByUuid: () => (userUuid) => {
      return userRepo.with("roles").where("uuid", userUuid).first();
    },

    isCurrentUserADeveloper: () => {
      return this.getCurrentUser.isDev();
    },

    /**
     * Get user by email
     * @param email
     * @returns {CertipersUser|null}
     */
    getUserByEmail: () => (email) => {
      return userRepo.where("email", email).first();
    },

    getSessionToRegisterPending: (state) => {
      return state.sessionToRegister;
    },

    getUsers: () => {
      return userRepo
        .where("isDisplayed", true)
        .withAllRecursive()
        .orderBy((user) => user.lastName.toUpperCase(), "asc")
        .get();
    },

    getUsersCount: (state) => {
      return state.itemsCount;
    },

    getUnreadNotificationCount: (state) => {
      return state.unreadNotificationCount;
    },

    getShouldCallDuplicatedUser: (state) => {
      return state.shouldCallDuplicatedUser;
    },

    getDisplayUserDuplicatedModal: (state) => {
      return state.displayUserDuplicatedModal;
    },
  },
});
